import { nanoid } from "nanoid";
import { SetDetailComponent } from "../set-detail/set-detail.component";
import { Catch } from "./catch.model";
import { Coordinate } from "./coordinate.model";
import { Gear } from "./gear.model";
import { LonglineGear } from "./longline-gear.model";
import { PelagicGear } from "./pelagic-gear.model";
import { PotGear } from "./pot-gear.model";

/**
 * Fishing event or set. An individual fishing event recording location, catch, gear used and so on.
 */
export class FishingEvent {

    //Is this a Groundfish set or an HMS one?
    fishery!: string;

    //Unique identifier for this fishing event
    fishingEventID: string = '';

    setNumber: string = '';

    //Links to a delivery
    landingID: string = '';

    //Links to a Gear item
    gear: Gear = new Gear();

    comments: string = '';

    startDate!: Date; //for HMS startdate will be the fishingDate

    startLat: Coordinate = new Coordinate; //for HMS startLat will be the mid-day latitude

    startLon: Coordinate = new Coordinate; //for HMS startLon will be the mid-day longitude

    startLatManualEntry!: boolean;

    startLonManualEntry!: boolean;

    catchItems: Catch[] = [];


    /* Groundfish only set properties */

    //Start and end depth are not used if gear on this set is Pelagic type, 'Depth off bottom' is used instead
    startDepth!: number;

    depthOffBottom!: number;

    endDate!: Date;

    endLat: Coordinate = new Coordinate;

    endLon: Coordinate = new Coordinate;

    endLatManualEntry!: boolean;

    endLonManualEntry!: boolean;

    endDepth!: number;

    //Pot and Longline gear types only?
    gearPerSet: number = 0;

    skateNum!: number;

    //Pot gear type only?
    lostPotCount!: number;

    baitType!: string;


    /* HMS Albacore only set properties */

    aircraftUsed!: boolean;

    hoursFished!: number;

    buoyCount!: number;

    hookCount!: number;

    fishingMode: string = '';  // string??


    constructor(name: string, fishery: string) {
        this.fishery = fishery;
        this.setNumber = name;
    }


  /**
   * Called to update the {@link SetDetailComponent.fishingEvent}
   * @param value Updated fishing event data
   */
  update(value: any) {
    this.skateNum = parseInt(value.skateNum);
    this.gear = value.gear;
    this.lostPotCount = parseInt(value.lostPotNum);
    this.startDate = value.startDate;
    this.endDate = value.endDate;
    this.startDepth = parseInt(value.startDepth);
    this.endDepth = parseInt(value.endDepth);
    this.depthOffBottom = parseInt(value.depthOffBottom);
    this.startLat = value.startLat;
    this.startLon = value.startLon;
    this.endLat = value.endLat;
    this.endLon = value.endLon;
    this.comments = value.comments;
    this.baitType = value.baitType;
  }

  updateHMS(value: any) {
    this.startDate = value.fishingDate;
    this.gear.type = value.gear; //Value coming through is of type lookupitem
    this.gear.gearSubtype = value.dsbgType;
    this.fishingMode = value.fishingMode;
    this.startLat = value.lat;
    this.startLon = value.lon;
    this.hoursFished = parseInt(value.hoursFished);
    this.buoyCount = parseInt(value.numBuoys);
    this.hookCount = parseInt(value.numHooks);
    this.aircraftUsed = value.aircraftUsed;
    this.comments = value.comments;
  }


  toString() {
    return this.setNumber == '' ? 'No Set/String' : this.setNumber;
  }


  static transformAnonymous(anon: any) {
    let fe = new FishingEvent(anon.setNumber, "");
    fe.fishery = anon.fishery ?? 'Groundfish';
    fe.fishingEventID = anon.fishingEventID ?? 'set-' + nanoid();
    fe.landingID = anon.landingID;
    fe.gear = fe.fishery == 'HMS' ? Gear.transformAnonymous(anon.gear) : 
        anon.gear.type == 'Pot' ? PotGear.transformAnonymous(anon.gear) :
        anon.gear.type == "Longline" ? LonglineGear.transformAnonymous(anon.gear) :
        anon.gear.type == "Pelagic" ? PelagicGear.transformAnonymous(anon.gear) : new Gear();
    fe.comments = anon.comments;
    fe.startDate = anon.startDate;
    fe.startLat = Coordinate.transformAnon(anon.startLat);
    fe.startLon = Coordinate.transformAnon(anon.startLon);
    fe.startLatManualEntry = anon.startLatManualEntry;
    fe.startLonManualEntry = anon.startLonManualEntry;
    fe.startDepth = anon.startDepth;
    fe.depthOffBottom = anon.depthOffBottom;
    fe.endDate = anon.endDate;
    fe.endLat = Coordinate.transformAnon(anon.endLat);
    fe.endLon = Coordinate.transformAnon(anon.endLon);
    fe.endLatManualEntry = anon.endLatManualEntry;
    fe.endLonManualEntry = anon.endLonManualEntry;
    fe.endDepth = anon.endDepth;
    fe.gearPerSet = anon.gearPerSet;
    fe.skateNum = anon.skateNum;
    fe.lostPotCount = anon.lostPotCount;
    fe.baitType = anon.baitType;
    fe.catchItems = Catch.transformAnonymous(anon.catchItems);
    fe.aircraftUsed = anon.aircraftUsed;
    fe.hoursFished = anon.hoursFished;
    fe.buoyCount = anon.buoyCount;
    fe.hookCount = anon.hookCount;
    fe.fishingMode = anon.fishingMode;
    return fe;
  }

}
